/**
 * Job Status Badge Component
 * Modern, condensed status display for maintenance jobs
 */

import React from 'react';
import { Box } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import {
  ErrorOutline as OverdueIcon,
  Schedule as PendingIcon,
  Notifications as NotifiedIcon,
  PlayArrow as InProgressIcon,
  CheckCircle as CompletedIcon,
  Cancel as CancelledIcon,
  SkipNext as SkippedIcon
} from '@material-ui/icons';

const useStyles = makeStyles((theme) => ({
  badge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
    padding: '2px 8px',
    borderRadius: 12,
    fontSize: 11,
    fontWeight: 600,
    letterSpacing: '0.3px',
    textTransform: 'uppercase',
    whiteSpace: 'nowrap',
    transition: 'all 0.2s ease',
  },
  icon: {
    fontSize: 14,
  },
  overdue: {
    backgroundColor: '#ffebee',
    color: '#c62828',
    border: '1px solid #ef5350',
  },
  pending: {
    backgroundColor: '#f5f5f5',
    color: '#616161',
    border: '1px solid #9e9e9e',
  },
  notified: {
    backgroundColor: '#e3f2fd',
    color: '#1565c0',
    border: '1px solid #42a5f5',
  },
  inProgress: {
    backgroundColor: '#fff3e0',
    color: '#e65100',
    border: '1px solid #fb8c00',
  },
  completed: {
    backgroundColor: '#e8f5e9',
    color: '#2e7d32',
    border: '1px solid #66bb6a',
  },
  cancelled: {
    backgroundColor: '#fafafa',
    color: '#757575',
    border: '1px solid #bdbdbd',
    textDecoration: 'line-through',
  },
  skipped: {
    backgroundColor: '#f5f5f5',
    color: '#9e9e9e',
    border: '1px solid #e0e0e0',
  },
}));

const JobStatusBadge = ({ status, clientNotified, engineerReminded, size = 'small' }) => {
  const classes = useStyles();

  // Determine display status
  const getDisplayStatus = () => {
    if (status === 'overdue') return { 
      label: 'Overdue', 
      class: classes.overdue, 
      icon: <OverdueIcon className={classes.icon} /> 
    };
    if (status === 'completed') return { 
      label: 'Complete', 
      class: classes.completed, 
      icon: <CompletedIcon className={classes.icon} /> 
    };
    if (status === 'cancelled') return { 
      label: 'Cancelled', 
      class: classes.cancelled, 
      icon: <CancelledIcon className={classes.icon} /> 
    };
    if (status === 'skipped') return { 
      label: 'Skipped', 
      class: classes.skipped, 
      icon: <SkippedIcon className={classes.icon} /> 
    };
    if (status === 'in_progress') return { 
      label: 'In Progress', 
      class: classes.inProgress, 
      icon: <InProgressIcon className={classes.icon} /> 
    };
    
    // Pending states with notification logic
    if (clientNotified || engineerReminded) {
      return { 
        label: 'Notified', 
        class: classes.notified, 
        icon: <NotifiedIcon className={classes.icon} /> 
      };
    }
    return { 
      label: 'Pending', 
      class: classes.pending, 
      icon: <PendingIcon className={classes.icon} /> 
    };
  };

  const displayStatus = getDisplayStatus();

  return (
    <Box className={`${classes.badge} ${displayStatus.class}`}>
      {displayStatus.icon}
      <span>{displayStatus.label}</span>
    </Box>
  );
};

export default JobStatusBadge;
